// ── 프론트엔드 공통 권한 훅 ──────────────────────────────────
// 저장 위치: C:\ims\frontend\src\hooks\usePermissions.js (frontend\src 아래 hooks 폴더 새로 생성)
//
// 동작 방식 (백엔드 middleware\permissions.js 와 반드시 동일한 규칙을 따릅니다)
// - 관리자/생산관리자/협력사 "역할" 개념은 없습니다. 계정은 최고관리자(superadmin) 또는
//   일반 계정(member) 둘 중 하나이며, 일반 계정의 모든 접근권한은 예외 없이 계정관리 >
//   권한설정에서 설정한 값을 따릅니다("권한설정 사용" 토글도 더 이상 사용하지 않습니다).
// - 로그인한 사용자 본인의 권한을 GET /api/permissions/me 로 1회 조회합니다.
// - 최고관리자(superadmin)는 항상 모든 메뉴/세부항목에 대해 true 를 반환합니다.
// - 그 외 모든 계정은 menu_permissions / user_menu_detail_permissions 에 저장된 값을
//   확인하며, 저장된 값이 없으면(한 번도 켜준 적 없으면) false(막힘)로 취급합니다.
//   → 백엔드 requireMenuPermission / checkDetailPermission 과 동일한 기본값(기본 차단)입니다.
// - 데이터 로딩 중(loading=true)에는 항상 true 를 반환하여, 로딩 전 깜빡이며
//   버튼이 숨겨졌다 나타나는 현상을 방지합니다. (실제 접근은 백엔드가 최종적으로 막아줍니다)

import { useState, useEffect, useCallback } from 'react';
import API from '../api';
import { useAuth } from '../context/AuthContext';

export function usePermissions() {
  const { user } = useAuth();
  const [state, setState] = useState({
    loading: true,
    enabled: false,
    permissions: [], // [{ menu_key, can_read, can_write }]
    details: [],      // [{ menu_key, detail_key, value }]
    // 서버 컴퓨터 앞에서 직접(localhost) 접속했는지 여부 — 백엔드 localOnly 미들웨어와 동일한
    // req.ip 기준 판정 결과(2026-08 추가). 프론트는 더 이상 window.location.hostname으로
    // 직접 판단하지 않고 이 값을 그대로 쓴다(App.js 참고).
    isLocal: false,
  });

  useEffect(() => {
    let alive = true;
    if (!user) {
      setState({ loading: false, enabled: false, permissions: [], details: [], isLocal: false });
      return;
    }
    API.get('/permissions/me')
      .then((res) => {
        if (!alive) return;
        const { enabled, permissions, details, isLocal } = res.data || {};
        setState({
          loading: false,
          enabled: !!enabled,
          permissions: Array.isArray(permissions) ? permissions : [],
          details: Array.isArray(details) ? details : [],
          isLocal: !!isLocal,
        });
      })
      .catch(() => {
        // 조회 실패 시에는 기존 동작(제한 없음)을 유지합니다. 실제 접근제어는 백엔드가 담당합니다.
        if (alive) setState({ loading: false, enabled: false, permissions: [], details: [], isLocal: false });
      });
    return () => { alive = false; };
  }, [user]);

  // role==='admin'(계정관리에서 최고관리자가 부여한 관리자)도 최고관리자와 동일한 권한을 가져야
  // 하므로(2026-08 추가), 이 훅의 모든 우회(bypass) 판단 기준이 되는 isSuperAdmin에 함께 포함한다.
  const isSuperAdmin = user?.role === 'superadmin' || user?.role === 'admin';

  const canRead = useCallback((menuKey) => {
    if (isSuperAdmin || state.loading) return true;
    const p = state.permissions.find((x) => x.menu_key === menuKey);
    return !!p?.can_read;
  }, [state, isSuperAdmin]);

  const canWrite = useCallback((menuKey) => {
    if (isSuperAdmin || state.loading) return true;
    const p = state.permissions.find((x) => x.menu_key === menuKey);
    return !!p?.can_write;
  }, [state, isSuperAdmin]);

  // 세부항목이 정의되지 않은 메뉴이거나 조회 실패 시에는 loading 처리로 흡수됨(위 state.loading)
  const hasDetail = useCallback((menuKey, detailKey) => {
    if (isSuperAdmin || state.loading) return true;
    const d = state.details.find((x) => x.menu_key === menuKey && x.detail_key === detailKey);
    if (!d) return false; // 백엔드와 동일: 설정된 적 없으면 막힘
    return d.value === '1';
  }, [state, isSuperAdmin]);

  // 체크박스(1/0)가 아니라 목록(예: 스토리지의 "접근가능 폴더")을 저장하는 세부항목용.
  // 백엔드 storage.js의 getAccessibleFolders와 동일한 규칙으로 반환합니다.
  //  - null  : 제한 없음(최고관리자이거나 로딩 중)
  //  - []    : 이 항목을 한 번도 저장한 적 없음 → 전부 차단
  //  - [...] : 저장된 허용 목록
  const getDetailList = useCallback((menuKey, detailKey) => {
    if (isSuperAdmin || state.loading) return null;
    const d = state.details.find((x) => x.menu_key === menuKey && x.detail_key === detailKey);
    if (!d) return [];
    try {
      const parsed = JSON.parse(d.value || '[]');
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }, [state, isSuperAdmin]);

  // getDetailList의 결과에서 특정 값 하나가 허용목록에 포함되는지만 바로 boolean으로 확인하는
  // 편의 함수 (예: hasListItem('contracts','category_write_scope', contract.contract_type)).
  const hasListItem = useCallback((menuKey, detailKey, value) => {
    if (isSuperAdmin || state.loading) return true;
    if (!value) return false;
    const list = getDetailList(menuKey, detailKey);
    if (list === null) return true;
    return list.includes(value);
  }, [getDetailList, isSuperAdmin, state.loading]);

  // 2026-08 버그수정 — isLocal을 state에는 담아뒀지만 정작 이 반환 객체에 포함하지 않아서,
  // 이 훅을 쓰는 PrivateRoute 등에서는 항상 isLocal이 undefined(=false 취급)로 읽혔다.
  // 그 결과 백엔드가 isLocal:true를 정확히 내려줘도 "사내 네트워크에서 접속했을 때만 사용할
  // 수 있습니다" 문구가 항상 떴다(사이드바는 이 훅을 안 쓰고 자체 fetch로 값을 바로 읽어서
  // 정상 표시됐던 것). 이제 state.isLocal을 그대로 함께 반환한다.
  return { loading: state.loading, canRead, canWrite, hasDetail, getDetailList, hasListItem, isLocal: state.isLocal };
}

export default usePermissions;
